import axios from 'axios'
import { createCustomEvent } from '@/assets/ts/customEvent'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'

const serviceList = ['ProductSharing', 'Buyforyou', 'ExpressCheckout']

function requestLogin({
	email,
	password,
	token,
	provider,
}: {
	email?: string
	password?: string
	token: string
	provider: 'basic' | 'email-otp' | 'facebook' | 'google' | 'apple'
}) {
	let parameters
	if (provider === 'basic') {
		parameters = {
			email,
			password,
			provider,
		}
	} else {
		parameters = {
			token,
			provider,
		}
	}

	return axios.post<{
		data: {
			user_token: string
			new_account?: string
		}
	}>(`${window.BUYANDSHIP.settings.api_url}sessions`, parameters)
}

function requestFacebookSDK(): any {
	if (window.FB === undefined) {
		console.error('Not import facebook sdk')
		return
	}

	clearToken()
	return new Promise((resolve, reject) => {
		// fb login only can test on staging or https://bns-assets.s3.amazonaws.com/websites/buyandship-2019/tests/..... link
		window.FB.login(
			(response: any) => {
				if (
					response &&
					response.authResponse &&
					response.authResponse.accessToken &&
					response.status === 'connected'
				) {
					const token = response.authResponse.accessToken
					requestLogin({ token, provider: 'facebook' })
						.then(({ data }) => {
							const responseData = data.data || {}
							const isNewUser =
								responseData.new_account && responseData.new_account === 'yes'
							if (isNewUser) {
								createCustomEvent('member_signed_up', { provider: 'facebook' })
							}
							resolve([responseData, isNewUser])
						})
						.catch(error => {
							reject(error)
						})
				} else {
					console.error('Cannot get user token')
					reject(new Error('Cannot get user token'))
				}
			},
			{ scope: 'public_profile,email' },
		)
	})
}

function requestGoogleSDK() {
	if (window.gapi === undefined) {
		console.error('Not import google sdk')
		return Promise.reject(new Error('Not import google sdk'))
	}

	// google login only can test on staging or https://bns-assets.s3.amazonaws.com/websites/buyandship-2019/tests/..... link
	return new Promise((resolve, reject) => {
		window.gapi.auth2
			.getAuthInstance()
			.signIn()
			.then(
				(response: any) => {
					if (response && response.wc && response.wc.access_token) {
						const token = response.wc.access_token
						requestLogin({ token, provider: 'google' }).then(({ data }) => {
							const responseData = data.data || {}
							if (
								responseData.new_account &&
								responseData.new_account === 'yes'
							)
								createCustomEvent('member_signed_up', { provider: 'google' })
							resolve(responseData)
						})
					} else {
						console.error('Cannot get user token')
						reject(new Error('Cannot get user token'))
					}
				},
				() => {
					console.error('Cannot get user token')
					reject(new Error('Cannot get user token'))
				},
			)
	})
}

function getClientId(): string {
	const googleSettingId =
		window.BUYANDSHIP.settings.member_auth.social_login.google.id
	const webClientId = googleSettingId.web
	return webClientId
}

function googleSignIn(successType = 'bns') {
	clearToken()
	const oauth2Endpoint = 'https://accounts.google.com/o/oauth2/v2/auth'
	const clientId = getClientId()
	const scopeUri =
		window.BUYANDSHIP.settings.member_auth.social_login.google.scope
	const redirectFromBackend =
		new URLSearchParams(window.location.search).get('redirect') || ''
	const stateCallback = {
		redirect:
			redirectFromBackend ||
			`${window.location.pathname}${window.location.search}`,
		successType,
	}

	const form = document.createElement('form')
	form.setAttribute('method', 'GET')
	form.setAttribute('action', oauth2Endpoint)

	const params = {
		client_id: clientId,
		redirect_uri: `${getSiteBaseUrl()}/?panel=login&status=google-login-success`,
		response_type: 'token',
		scope: scopeUri,
		include_granted_scopes: 'true',
		state: encodeURIComponent(JSON.stringify(stateCallback)),
	}

	Object.keys(params).forEach(key => {
		const input = document.createElement('input')
		input.setAttribute('type', 'hidden')
		input.setAttribute('name', key)
		input.setAttribute('value', params[key as keyof typeof params])
		form.appendChild(input)
	})

	document.body.appendChild(form)
	form.submit()
}

function clearToken() {
	let regex = ''
	const keys = serviceList.map(item => `token${item}`)
	keys.forEach((item, index) => {
		regex += `${item}${index >= keys.length - 1 ? '' : '|'}`
	})
	regex = `^(${regex})`
	Object.keys(localStorage)
		.filter(item => new RegExp(regex).test(item))
		.forEach(key => {
			localStorage.removeItem(key)
		})
}

export default {
	requestLogin,
	requestFacebookSDK,
	requestGoogleSDK,
	googleSignIn,
	clearToken,
}
